import React from 'react';
import { Drawer } from '@/components/ui/Drawer';
import { Badge, Button } from '@/components/ui';
import { X, User, Database, Clock, Globe, Shield, Terminal, ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ActivityDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  log: any;
}

export function ActivityDetailDrawer({ isOpen, onClose, log }: ActivityDetailDrawerProps) {
  if (!log) return null;

  const renderDataDiff = (label: string, data: any) => {
    if (!data || Object.keys(data).length === 0) return null;
    return (
      <div className="space-y-2">
        <p className="text-xs font-normal text-slate-500  tracking-wider">{label}</p>
        <div className="rounded-md bg-slate-50 p-3 overflow-auto max-h-[300px]">
          <pre className="text-[11px] font-mono text-slate-700 leading-relaxed whitespace-pre-wrap">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg" position="right" hideCloseButton>
      <div className="h-full flex flex-col bg-white">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Terminal className="w-5 h-5" />
             </div>
             <div>
                <h2 className="sm:text-sm text-xs font-normal text-slate-900">Chi tiết hoạt động</h2>
                <p className="text-[10px] text-slate-400 font-normal  tracking-tight">ID: {log.id}</p>
             </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Summary Card */}
          <div className="rounded-md border border-slate-100 bg-slate-50/50 p-4">
             <p className="sm:text-sm text-xs text-slate-900 leading-relaxed font-normal">
                {log.summary}
             </p>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-y-6 gap-x-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-slate-400 mb-1">
                <User className="w-3.5 h-3.5" />
                <span className="text-[10px]  tracking-wider font-normal">Người thực hiện</span>
              </div>
              <p className="sm:text-sm text-xs text-slate-900">{log.actorName}</p>
              <p className="text-[11px] text-slate-500">{log.actorEmail}</p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-slate-400 mb-1">
                <Clock className="w-3.5 h-3.5" />
                <span className="text-[10px]  tracking-wider font-normal">Thời gian</span>
              </div>
              <p className="sm:text-sm text-xs text-slate-900">
                {new Date(log.createdAt).toLocaleString('vi-VN')}
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-slate-400 mb-1">
                <Database className="w-3.5 h-3.5" />
                <span className="text-[10px]  tracking-wider font-normal">Phân hệ & Hành động</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] ">{log.moduleCode}</Badge>
                <ArrowRight className="w-3 h-3 text-slate-300" />
                <Badge className="text-[10px] ">{log.actionCode}</Badge>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-slate-400 mb-1">
                <Globe className="w-3.5 h-3.5" />
                <span className="text-[10px]  tracking-wider font-normal">Thông tin mạng</span>
              </div>
              <p className="sm:text-sm text-xs text-slate-900">{log.ipAddress || 'Không có dữ liệu'}</p>
              <p className="text-[11px] text-slate-500 truncate max-w-[150px]">{log.browser} on {log.os}</p>
            </div>
          </div>

          {/* Data Changes */}
          <div className="space-y-6 pt-4 border-t border-slate-100">
            {renderDataDiff('Dữ liệu trước khi thay đổi (Before)', log.beforeData)}
            {renderDataDiff('Dữ liệu sau khi thay đổi (After)', log.afterData)}
            
            {(!log.beforeData && !log.afterData) && (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                <Shield className="w-8 h-8 mb-2 opacity-20" />
                <p className="text-[11px]">Hoạt động này không ghi lại thay đổi dữ liệu chi tiết</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex justify-end">
          <Button variant="outline" onClick={onClose} className="h-9 px-6 sm:text-sm text-xs">
            Đóng
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
